import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TSK-204 — Centro de Mando CFO
 * Lee la vista `gastos_huerfanos_view` (RLS heredada vía security_invoker)
 * y mantiene la cache sincronizada con realtime sobre `gastos_embarque`.
 */

export type TasaFuente = "NATIVE_COP" | "TRM_PAGO" | "TRM_HISTORICO" | "MISSING";

export interface GastoHuerfanoRow {
  gasto_id: string;
  org_id: string;
  documento_id: string | null;
  concepto: string;
  monto: number;
  moneda: string;
  trm_pago: number | null;
  base_prorrateo: "Peso" | "Valor";
  aplica_costeo_inventario: boolean;
  notas: string | null;
  gasto_created_at: string;
  no_documento: string | null;
  tipo_documento: string | null;
  emisor: string | null;
  receptor: string | null;
  documento_fecha: string | null;
  documento_embarque_id: string | null;
  monto_cop: number | null;
  tasa_aplicada: number | null;
  tasa_fuente: TasaFuente;
}

export function useGastosHuerfanos() {
  const { user, orgId } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["gastos-huerfanos", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gastos_huerfanos_view" as any)
        .select("*")
        .eq("org_id", orgId!)
        .order("gasto_created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as GastoHuerfanoRow[];
    },
    enabled: !!user && !!orgId,
  });

  // Realtime: invalida cuando cambian gastos_embarque del tenant
  useEffect(() => {
    if (!orgId) return;
    const channel = supabase
      .channel(`gastos-huerfanos-${orgId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "gastos_embarque",
          filter: `org_id=eq.${orgId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["gastos-huerfanos", orgId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, qc]);

  return query;
}

/** RPC: asignar gastos huérfanos a un embarque (atómico, validado en backend) */
export function useAssignGastosToEmbarque() {
  const qc = useQueryClient();
  const { orgId } = useAuth();

  return useMutation({
    mutationFn: async (input: { gasto_ids: string[]; embarque_id: string }) => {
      const { data, error } = await supabase.rpc("assign_gastos_to_embarque" as any, {
        p_gasto_ids: input.gasto_ids,
        p_embarque_id: input.embarque_id,
      });
      if (error) throw error;
      return data as { success: boolean; gastos_asignados: number; embarque_id: string };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["gastos-huerfanos", orgId] });
      qc.invalidateQueries({ queryKey: ["gastos-embarque", vars.embarque_id] });
      qc.invalidateQueries({ queryKey: ["embarques"] });
    },
  });
}
